import React, { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Header from "../components/Header";
import HeroSpline from "../components/HeroSpline";
import HeroCosmic from "../components/HeroCosmic";
import WorkCarousel from "../components/WorkCarousel";
import { testimonials, trustPoints, processSteps } from "../mock/data";
import { useNavigate } from "react-router-dom";

const Home = () => {
  const [heroVersion, setHeroVersion] = useState("spline"); // 'spline' or 'cosmic'
  const navigate = useNavigate();

  return (
    <div className="bg-black text-white min-h-screen">
      <Header />

      {/* Render selected hero version */}
      {heroVersion === "spline" ? <HeroSpline /> : <HeroCosmic />}

      {/* Trust & Experience Section */}
      <section className="relative bg-black py-24 md:py-32 px-[7.6923%]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            {trustPoints.map((point) => (
              <div
                key={point.id}
                className="space-y-4 border-l-2 border-[#00FFD1] pl-6 transition-all duration-400 hover:border-opacity-100 border-opacity-50"
              >
                <h3 className="text-2xl md:text-3xl font-semibold text-white leading-tight">
                  {point.title}
                </h3>
                <p className="text-lg md:text-xl text-white text-opacity-85 leading-relaxed">
                  {point.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="relative bg-black py-24 md:py-32 px-[7.6923%] border-t border-white border-opacity-10">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-4xl md:text-5xl font-semibold text-white mb-16 text-center">
            Trusted by local businesses
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white bg-opacity-5 border border-white border-opacity-25 p-8 md:p-10 space-y-6 transition-all duration-400 hover:border-[#00FFD1] hover:border-opacity-50"
              >
                <p className="text-xl md:text-2xl text-white leading-relaxed font-medium">
                  "{testimonial.content}"
                </p>
                <div className="pt-4 border-t border-white border-opacity-20">
                  <p className="text-lg text-white font-semibold">
                    {testimonial.name}
                  </p>
                  <p className="text-base text-white text-opacity-70">
                    {testimonial.business}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Work Examples Section */}
      <section
        id="work"
        className="relative bg-black py-24 md:py-32 px-[7.6923%] border-t border-white border-opacity-10"
      >
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-4xl md:text-5xl font-semibold text-white mb-16 text-center">
            Some of our recent work
          </h2>

          <WorkCarousel />
        </div>
      </section>

      {/* How It Works Section */}
      <section
        id="process"
        className="relative bg-black py-24 md:py-32 px-[7.6923%] border-t border-white border-opacity-10"
      >
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-4xl md:text-5xl font-semibold text-white mb-16 text-center">
            How it works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {processSteps.map((step, index) => (
              <div key={step.id} className="relative space-y-6">
                <div className="text-[#00FFD1] text-6xl font-bold opacity-30">
                  {step.number}
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold text-white leading-tight">
                  {step.title}
                </h3>
                <p className="text-lg text-white text-opacity-85 leading-relaxed">
                  {step.description}
                </p>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute -right-8 top-1/2 -translate-y-1/2">
                    <ArrowRight
                      size={32}
                      className="text-white text-opacity-20"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative bg-black py-32 md:py-40 px-[7.6923%] border-t border-white border-opacity-10 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-10"
            style={{
              background:
                "radial-gradient(circle, rgba(0, 255, 209, 0.4) 0%, transparent 70%)",
            }}
          />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto text-center space-y-12">
          <div className="space-y-6">
            <h2 className="text-5xl md:text-6xl font-semibold text-white leading-tight">
              Need a website? Just email us.
            </h2>
            <p className="text-xl md:text-2xl text-white text-opacity-85 max-w-3xl mx-auto leading-relaxed">
              No forms. No sales calls. Just tell us what you need.
            </p>
          </div>

          <a
            href="mailto:hello@frontroom.website"
            className="inline-flex items-center gap-4 bg-[#00FFD1] text-black px-8 py-5 text-2xl font-semibold transition-all duration-400 hover:bg-white hover:scale-[1.02] hover:shadow-2xl shadow-[0_0_40px_rgba(0,255,209,0.3)]"
          >
            hello@frontroom.website
            <ArrowRight size={28} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-black py-12 px-[7.6923%] border-t border-white border-opacity-10">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_c2ff8b63-a192-43df-8c8f-347292d98479/artifacts/lq0zjqwg_ChatGPT%20Image%20Dec%2019%2C%202025%2C%2012_22_40%20AM.png"
              alt="SURFACE Labs"
              className="h-8 object-contain"
            />
          </div>
          <p className="text-white text-opacity-50 text-base">
            © 2025 Frontroom. Building websites that work.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
